/* Wizard · Blocks (preview cards + pricing/priority + uploaders + callouts)
 * Extracted iter101.91 from `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx`.
 *
 * Contents (roughly grouped):
 *   Pricing pickers   → TierPicker · TierFeature · PriorityPicker
 *                       · IOScannerToggleCard · LabVisitScanFeeRow
 *                       · PromoBlock · PricingAndOffersCard
 *   Preview cards     → PrescriptionPreviewCard · PreviewBlock · PreviewRow
 *                       · CoeOrderPreviewCard
 *   Uploaders         → SlotUploader · PlanningSoftwareExportCard
 *   Callouts          → CoeHandoffCallout
 */
import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { API } from "@/lib/api";
import {
    CaretRight, CaretLeft, CaretDown, CheckCircle, Circle, Upload, X, Check,
    MagnifyingGlass, ArrowsCounterClockwise, Crown, FileArrowUp, GitBranch,
    Warning, Lightning, Clock, Info, Tooth, Star, ShieldCheck, Cube, Cloud,
    CloudArrowUp, File as FileIcon, FilePdf, FileZip, FileVideo, Sparkle, Tag,
    ClipboardText, HeartStraight, Ruler, TrendUp, Sliders, Package, Prohibit,
    Notepad, ListBullets, Pencil, ArrowSquareUp, ArrowSquareDown, CircleHalf,
    Printer, BookmarkSimple, FloppyDisk, Trash, ChartBar, Hourglass, Calculator,
    Lock, UserGear, Camera, Stethoscope, HouseLine, Plus, Prescription,
    Headset, Truck, Receipt, SealCheck, Buildings, Hash, Scan, ArrowRight,
    User as UserIcon,
} from "@phosphor-icons/react";
import AdvancePaymentBlock from "@/components/case/AdvancePaymentBlock";
import PrescriptionComplianceBlock from "@/components/case/PrescriptionComplianceBlock";
import PatientAvatar from "@/components/patient/PatientAvatar";
import InvisiqueLogo from "@/components/site/Logo";
import {
    SelectedPill, ReviewBlock, Row, Step1SectionHeader,
    cardBadgeClass, resolveHeroIcon, fmtINR,
} from "@/components/case/wizard/shared";
import { ModeCard } from "@/components/case/wizard/pickers";
import { useSettlePulse } from "@/components/case/wizard/hooks";


export function IOScannerToggleCard({ pricing, setPricing, fee, step = 1 }) {
    const checked = !!pricing.scanner_pickup_requested;
    /* iter98.99ai — Shared settle-pulse. Toggle cards need the same
       "just picked" feedback as radio/mode cards. Amber ring to
       match the "EXCLUSIVE" ribbon on this card. */
    const flashCls = useSettlePulse(checked, "ring-amber-400/35");
    const onToggle = (next) => {
        setPricing((p) => {
            const newScannerFee = next ? Number(fee || 0) : 0;
            const lab = Number(p.lab_charge || 0);
            const priorityFee = Number(p.priority_fee || 0);
            const discount = Number(p.discount_amount || 0);
            return {
                ...p,
                scanner_pickup_requested: next,
                scanner_pickup_fee: newScannerFee,
                // Re-run the running total whenever the toggle flips so
                // the Step 1 + Step 7 pricing summaries reflect the
                // add-on immediately. Discounts are subtracted last per
                // the existing convention.
                final_charge: Math.max(0, lab + priorityFee + newScannerFee - discount),
            };
        });
    };
    return (
        <button
            type="button"
            onClick={() => onToggle(!checked)}
            className={`relative w-full text-left rounded-2xl border-2 transition-all duration-300 overflow-hidden ${flashCls} ${
                checked
                    ? "border-primary bg-gradient-to-br from-primary/[0.10] via-primary/[0.04] to-primary/[0.08] shadow-lg shadow-primary/10"
                    : "border-primary/25 bg-gradient-to-br from-primary/[0.04] via-card to-primary/[0.03] hover:shadow-xl hover:-translate-y-0.5 hover:border-primary/55"
            }`}
            data-testid={`io-scanner-toggle-step-${step}`}
            aria-pressed={checked}
        >
            {/* iter97.71 → iter98.99p — Floating "EXCLUSIVE" ribbon
                badge. Half-outside the top-right of the card (matches
                the ModeCard / Tier Picker pattern). Amber gradient +
                sparkle icon marks this as a differentiator no
                competitor offers. */}
            <span className="absolute -top-2.5 right-3 z-10 inline-flex items-center gap-1 px-3 py-[3px] rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-white text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap shadow-md shadow-amber-500/30 ring-1 ring-white/70">
                <Sparkle size={10} weight="fill" /> Exclusive
            </span>
            {/* Soft glow blob top-left — adds depth without being noisy */}
            <div className="absolute -top-16 -left-12 w-44 h-44 rounded-full bg-primary/15 blur-3xl pointer-events-none" aria-hidden />
            {/* Top section: icon + title-block + price */}
            <div className="relative flex items-start gap-4 px-4 pt-4 pb-3">
                <div
                    className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                        checked
                            ? "bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg shadow-primary/30"
                            : "bg-gradient-to-br from-primary/20 to-primary/[0.06] text-primary"
                    }`}
                >
                    <Tooth size={28} weight={checked ? "fill" : "duotone"} />
                </div>
                <div className="flex-1 min-w-0 pr-24">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/80">
                        Premium add-on
                    </div>
                    <h3 className="font-display text-[16px] font-bold leading-tight mt-0.5">
                        IO Scans by Invisique™
                    </h3>
                    <p className="text-[12px] text-foreground/75 mt-1 leading-snug">
                        No intraoral scanner in your clinic? Our certified technician visits with hospital-grade equipment and captures pristine digital records for you — no setup, no investment.
                    </p>
                </div>
                <div className="absolute top-12 right-4 flex flex-col items-end">
                    <span className="font-display text-xl font-bold tabular-nums text-primary leading-none">
                        +{fmtINR(Number(fee || 0))}
                    </span>
                    <span className="text-[9.5px] text-muted-foreground uppercase tracking-wider mt-1">one-time fee</span>
                </div>
            </div>
            {/* Bottom strip — 3 included benefits + Added confirmation */}
            <div className="relative px-4 py-2.5 bg-white/60 border-t border-primary/15 flex items-center flex-wrap gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
                    <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                    Same-day scheduling
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
                    <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                    Trios / iTero quality
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
                    <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                    Auto-attached to case
                </span>
                {checked && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-primary">
                        <CheckCircle size={13} weight="fill" /> Added — we&apos;ll reach out to schedule
                    </span>
                )}
            </div>
        </button>
    );
}

/* iter98.99o — `StepChooseTier` (Smart Tier Picker for self-plan) was
   retired. Self-Plan tier is now auto-assigned from Lab Instructions
   aligner counts (see `autoSelfPlanTier` memo above) and CoE cases
   have their tier picked by planners. `TierPicker` below is still
   used inside Step 6 Review + StepReview to render the tier grid. */


export function TierPicker({ tiers, pricing, setPricing, tierInsights = null, planningMode = "coe", authoredCount = 0, recommendedTierId = null }) {
    // iter98.99au — Effective lab charge respects discount_type (flat ₹ or %).
    const effectivePrice = (tier) => {
        const raw = Number(tier.lab_charge || 0);
        if (planningMode !== "doctor") return raw;
        const d = Number(tier.self_plan_discount || 0);
        const t = (tier.self_plan_discount_type || "flat").toLowerCase();
        return t === "percent"
            ? Math.max(0, raw * (1 - d / 100))
            : Math.max(0, raw - d);
    };
    const onPick = (tier) => {
        setPricing((p) => {
            // iter92.10 — preserve any priority fee already set; final_charge
            // includes lab - discount + priority_fee. Promo resets to 0 on
            // tier change (different lab_charge → old promo math invalid).
            const fee = Number(p.priority_fee || 0);
            const newLab = effectivePrice(tier);
            return {
                ...p,
                tier_id: tier.id,
                tier_name: tier.name,
                lab_charge: newLab,
                promo_id: null, promo_code: "", discount_type: null,
                discount_value: 0, discount_amount: 0,
                final_charge: Math.max(0, newLab + fee + Number(p.scanner_pickup_fee || 0)),
            };
        });
    };
    if (!tiers || tiers.length === 0) {
        return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                No active treatment tiers found. Ask your admin to set them up under Pricing.
            </div>
        );
    }
    // iter92 — dynamic favourite-tier badge fallback. Admin's card_badge
    // always wins; this kicks in only when the admin hasn't customised the
    // badge AND the doctor has ≥3 cases on that tier.
    const favouriteTierId = tierInsights?.favourite_tier_id || null;
    return (
        <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-3" data-testid="case-wizard-tier-picker">
                {tiers.map((t) => {
                    const selected = pricing.tier_id === t.id;
                    // iter92 — admin-editable card content. Fall back to
                    // auto-generated bullets when admin hasn't customised.
                    const customBullets = (t.card_bullets || []).filter((b) => (b || "").trim());
                    const bullets = customBullets.length > 0 ? customBullets : [
                        `Up to ${t.aligner_count} aligners`,
                        t.refinement_included
                            ? `Refinement up to ${t.refinement_aligner_count}`
                            : "No refinement",
                        t.retainers_included ? "Retainers included" : "No retainers",
                    ];
                    // Compute the badge to show: admin override > dynamic favourite > none.
                    const adminBadge = (t.card_badge || "").trim();
                    const dynamicBadge = !adminBadge && favouriteTierId === t.id ? "YOUR FAVOURITE" : "";
                    // iter97.50 — Smart-pick ribbons. When the caller passes
                    // `recommendedTierId` (Choose-Tier step), the matching
                    // tier wins the badge slot. Tiers whose cap is below the
                    // authored count get a subtle red "OVERAGE" badge so the
                    // doctor sees the cost implication at a glance.
                    const tierCap = Number(t.aligner_count) || 0;
                    const overageHere = authoredCount > 0 && tierCap > 0 && tierCap < authoredCount;
                    const recommendedHere = recommendedTierId === t.id;
                    const smartBadge = recommendedHere
                        ? "RECOMMENDED"
                        : overageHere
                            ? `OVERAGE +${authoredCount - tierCap}`
                            : "";
                    const badge = smartBadge || adminBadge || dynamicBadge;
                    const accentRing = {
                        primary: "border-primary ring-2 ring-primary/15 bg-gradient-to-br from-primary/[0.04] to-primary/[0.10] shadow-sm",
                        emerald: "border-emerald-500 ring-2 ring-emerald-500/15 bg-gradient-to-br from-emerald-50 to-emerald-100/60 shadow-sm",
                        amber: "border-amber-500 ring-2 ring-amber-500/15 bg-gradient-to-br from-amber-50 to-amber-100/60 shadow-sm",
                        violet: "border-violet-500 ring-2 ring-violet-500/15 bg-gradient-to-br from-violet-50 to-violet-100/60 shadow-sm",
                    }[t.card_accent || "primary"] || "border-primary ring-2 ring-primary/15 bg-gradient-to-br from-primary/[0.04] to-primary/[0.10] shadow-sm";
                    const badgeClass = recommendedHere
                        ? "bg-emerald-600 text-white"
                        : overageHere
                            ? "bg-amber-500 text-white"
                            : dynamicBadge
                                // Dynamic favourite badge always uses an accent that contrasts
                                // with the tier's main accent to feel like a personal sticker
                                // and not a marketing chip.
                                ? "bg-amber-500 text-white"
                                : {
                                    primary: "bg-primary text-white",
                                    emerald: "bg-emerald-600 text-white",
                                    amber: "bg-amber-500 text-white",
                                    violet: "bg-violet-600 text-white",
                                }[t.card_accent || "primary"] || "bg-primary text-white";
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => onPick(t)}
                            data-testid={`tier-pick-${t.id}`}
                            className={`relative text-left rounded-xl border px-3 pt-5 pb-3 transition-all duration-200 ${
                                selected
                                    ? accentRing
                                    : "border-border hover:border-primary/50 hover:shadow-md hover:-translate-y-0.5 bg-card"
                            }`}
                        >
                            {/* iter97.76 → iter98.99p — Floating ribbon
                                badge (half-outside / half-inside the
                                top-right of the card). Stays visible
                                when the tier is selected so the doctor
                                always sees why it's recommended. Style
                                synced with the ModeCard badge for
                                visual consistency. */}
                            {!!badge && (
                                <span
                                    className={`absolute -top-2.5 right-3 inline-flex items-center gap-1 px-3 py-[3px] rounded-full text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap shadow-md ring-1 ring-white/70 ${badgeClass} z-10`}
                                    data-testid={`tier-pick-${t.id}-badge`}
                                >
                                    {badge}
                                </span>
                            )}
                            {/* iter98.99s — Top-left CheckCircle removed.
                                The inline SELECTED pill beside the tier
                                name is now the single confirmation. */}
                            <div className="flex items-baseline justify-between gap-2 pr-6">
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="font-display text-[15px] font-bold leading-none truncate">{t.name}</div>
                                    {selected && <SelectedPill accent={t.card_accent || "primary"} testid={`tier-pick-${t.id}-selected-pill`} />}
                                </div>
                                <div className="font-display text-lg font-bold tabular-nums text-primary leading-none">
                                    {fmtINR(effectivePrice(t))}
                                </div>
                            </div>
                            {/* iter98.99aq — self-plan discount inline savings copy */}
                            {planningMode === "doctor" && Number(t.self_plan_discount || 0) > 0 && (
                                <div className="text-[10px] text-emerald-700 font-semibold mt-1" data-testid={`tier-pick-${t.id}-self-plan-savings`}>
                                    <span className="line-through text-muted-foreground font-normal mr-1">{fmtINR(t.lab_charge)}</span>
                                    Save {fmtINR(t.self_plan_discount)} (self-plan)
                                </div>
                            )}
                            {!!t.card_subtitle && (
                                <div
                                    className="text-[11px] text-muted-foreground mt-1 font-medium"
                                    data-testid={`tier-pick-${t.id}-subtitle`}
                                >
                                    {t.card_subtitle}
                                </div>
                            )}
                            <div className="mt-2 space-y-1">
                                {bullets.map((b, i) => (
                                    <TierFeature key={i} on label={b} />
                                ))}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}


export function TierFeature({ on, label }) {
    return (
        <div className="flex items-center gap-2 text-xs">
            {on ? (
                <CheckCircle size={14} weight="fill" className="text-primary shrink-0" />
            ) : (
                <X size={14} weight="bold" className="text-muted-foreground/50 shrink-0" />
            )}
            <span className={on ? "text-foreground/85" : "text-muted-foreground"}>{label}</span>
        </div>
    );
}


/* iter98.59 — Lab-visit scanning fee line in Pricing & Offers.
 *
 * Simplified from the previous tri-state (deferred / due / paid) to a
 * single "deferred → added to final invoice" mode. The
 * "Pay separately / upfront" option was removed from the booking flow
 * at the user's request — every new lab-visit fee now rides along on
 * the case's final invoice. We keep this row visible (instead of
 * hiding it) because the doctor still benefits from seeing the fee
 * surfaced as a line item before final billing.
 */
export function LabVisitScanFeeRow({ summary }) {
    if (!summary || !summary.fee) return null;

    const fee = Number(summary.fee || 0);

    return (
        <div className="px-5 pb-4" data-testid="pricing-row-lab-visit-scan">
            <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-sm text-foreground/80">Invisique™ Lab Scanning</div>
                    <div className="text-[10px] uppercase tracking-wider mt-0.5">
                        <span className="text-muted-foreground">Added to your final invoice</span>
                    </div>
                </div>
                <div className="shrink-0 flex items-center gap-3">
                    <span
                        className="font-display text-sm font-bold tabular-nums text-primary"
                        data-testid="lab-visit-scan-fee-deferred"
                    >
                        + {fmtINR(fee)}
                    </span>
                </div>
            </div>
        </div>
    );
}



export function PromoBlock({ pricing, setPricing, promotions = [] }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [code, setCode] = useState(pricing.promo_code || "");
    const [busy, setBusy] = useState(false);
    const applied = !!pricing.promo_id;

    const eligiblePromos = useMemo(() => {
        if (!pricing.tier_id) return [];
        return (promotions || []).filter((p) => {
            const list = p.applicable_tier_ids || [];
            return list.length === 0 || list.includes(pricing.tier_id);
        });
    }, [promotions, pricing.tier_id]);

    const applyCode = async (c) => {
        const clean = (c || "").trim().toUpperCase();
        if (!clean) { toast.error("Enter a promo code"); return; }
        if (!pricing.tier_id) { toast.error("Pick a tier first"); return; }
        setBusy(true);
        try {
            const r = await api.post("/promotions/validate", {
                code: clean, tier_id: pricing.tier_id, lab_charge: pricing.lab_charge || 0,
            });
            setPricing((p) => {
                // iter92.10 — include any existing priority_fee in final_charge.
                // Server returns `final_charge = lab - discount`; we add the
                // priority surcharge on top so the wizard total matches the
                // doctor's actual bill.
                const fee = Number(p.priority_fee || 0);
                return {
                    ...p,
                    promo_id: r.data.promo_id,
                    promo_code: r.data.code,
                    discount_type: r.data.discount_type,
                    discount_value: r.data.discount_value,
                    discount_amount: r.data.discount_amount,
                    final_charge: Math.max(0, Number(r.data.final_charge || 0) + fee),
                };
            });
            setCode(clean);
            toast.success(`Applied: ${r.data.name}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Code not valid");
        } finally { setBusy(false); }
    };

    /* iter98.33 — Honor `?promo=<CODE>` query param coming from the Offers
       page "Use on new case" button. Auto-apply once tier is selected, then
       strip the param so a refresh / step-change doesn't double-apply. */
    const promoFromUrl = searchParams.get("promo");
    useEffect(() => {
        if (!promoFromUrl || applied || busy || !pricing.tier_id) return;
        applyCode(promoFromUrl);
        const p = new URLSearchParams(searchParams);
        p.delete("promo");
        setSearchParams(p, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricing.tier_id, promoFromUrl, applied]);

    const apply = () => applyCode(code);

    const clear = () => {
        setCode("");
        setPricing((p) => {
            // iter92.10 — final_charge = lab + priority_fee when no discount.
            const fee = Number(p.priority_fee || 0);
            const lab = Number(p.lab_charge || 0);
            return {
                ...p,
                promo_id: null, promo_code: "", discount_type: null,
                discount_value: 0, discount_amount: 0,
                final_charge: Math.max(0, lab + fee),
            };
        });
    };

    if (!pricing.tier_id) return null;

    return (
        <div className="bg-secondary/30 border border-border rounded-xl p-4 space-y-3" data-testid="case-wizard-pricing-card">
            <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Lab charge</span>
                <span className="tabular-nums font-semibold">{fmtINR(pricing.lab_charge)}</span>
            </div>
            {applied && (
                <div className="flex items-center justify-between text-sm">
                    <span className="text-primary flex items-center gap-1.5">
                        <CheckCircle size={13} weight="fill" /> {pricing.promo_code}
                        <span className="text-[11px] text-muted-foreground">
                            ({pricing.discount_type === "percentage" ? `${pricing.discount_value}%` : fmtINR(pricing.discount_value)} off)
                        </span>
                    </span>
                    <span className="tabular-nums font-semibold text-primary">- {fmtINR(pricing.discount_amount)}</span>
                </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-sm font-bold">Final charge</span>
                <span className="font-display text-lg font-bold tabular-nums">{fmtINR(pricing.final_charge)}</span>
            </div>

            {applied ? (
                <Button type="button" variant="ghost" size="sm" onClick={clear} className="w-full text-rose-700 hover:bg-rose-50" data-testid="case-wizard-promo-clear">
                    Remove promo code
                </Button>
            ) : (
                <div className="space-y-2.5 pt-1">
                    {eligiblePromos.length > 0 && (
                        <div data-testid="case-wizard-promo-chips">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1.5">
                                Live offers · tap to apply
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {eligiblePromos.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        disabled={busy}
                                        onClick={() => applyCode(p.code)}
                                        data-testid={`promo-chip-${p.code}`}
                                        className="group inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/60 px-3 py-1.5 text-[11px] font-semibold text-primary transition disabled:opacity-50"
                                    >
                                        <span className="font-mono tracking-wider">{p.code}</span>
                                        <span className="text-primary/70 font-medium">
                                            · {p.discount_type === "percentage" ? `${p.discount_value}% off` : `${fmtINR(p.discount_value)} off`}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Input
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder={eligiblePromos.length > 0 ? "Or type a code" : "Promo code (optional)"}
                            className="h-9 font-mono uppercase tracking-wider"
                            data-testid="case-wizard-promo-input"
                        />
                        <Button type="button" size="sm" onClick={apply} disabled={busy || !code.trim()} data-testid="case-wizard-promo-apply">
                            {busy ? "Checking…" : "Apply"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}


/* ─────── Pricing & offers card (iter94 — unified card) ───────
 * Single card sitting at the bottom of Step 1 that merges:
 *  - Tier row (name + lab_charge)
 *  - Manufacturing speed row (Included for Standard; +fee for Rush/STAT)
 *  - Live offer chips + promo code input (auto-discovered from /promotions/active)
 *  - Applied discount line
 *  - Total
 * Renders only once a tier is picked (parent gates this on pricing.tier_id).
 */
export function PricingAndOffersCard({ pricing, setPricing, promotions = [], scanFeeSummary = null }) {
    const [code, setCode] = useState(pricing.promo_code || "");
    const [busy, setBusy] = useState(false);
    const applied = !!pricing.promo_id;

    const lab = Number(pricing?.lab_charge || 0);
    const discount = Number(pricing?.discount_amount || 0);
    const priorityFee = Number(pricing?.priority_fee || 0);
    // iter97.51 — Invisique IO Scanning Service is an add-on that flows
    // into the running total just like priority. We surface it as its
    // own line item below and add it to `final` so the displayed Total
    // matches the doctor's actual invoice.
    const scannerFee = Number(pricing?.scanner_pickup_fee || 0);
    const scannerRequested = !!pricing?.scanner_pickup_requested;
    // iter98.58 — Lab-visit scanning fee. When the doctor chose "add
    // to final bill" at scan-visit booking, the fee gets billed by
    // Invisique at production time — we include it in the running
    // Total here so the displayed number matches what the doctor will
    // actually owe. When they chose "pay separately at booking"
    // (upfront), the fee is its own collectible invoice and stays
    // OUT of this Total (see LabVisitScanFeeRow for the separate
    // settle CTA).
    const labVisitDeferredFee = (scanFeeSummary && scanFeeSummary.status === "deferred")
        ? Number(scanFeeSummary.fee || 0)
        : 0;
    const final = Math.max(0, lab - discount + priorityFee + scannerFee + labVisitDeferredFee);

    const eligiblePromos = useMemo(() => {
        if (!pricing.tier_id) return [];
        return (promotions || []).filter((p) => {
            const list = p.applicable_tier_ids || [];
            return list.length === 0 || list.includes(pricing.tier_id);
        });
    }, [promotions, pricing.tier_id]);

    const applyCode = async (raw) => {
        const clean = (raw || "").trim().toUpperCase();
        if (!clean) { toast.error("Enter a promo code"); return; }
        if (!pricing.tier_id) { toast.error("Pick a tier first"); return; }
        setBusy(true);
        try {
            const r = await api.post("/promotions/validate", {
                code: clean, tier_id: pricing.tier_id, lab_charge: pricing.lab_charge || 0,
            });
            setPricing((p) => {
                const fee = Number(p.priority_fee || 0);
                const sFee = Number(p.scanner_pickup_fee || 0);
                return {
                    ...p,
                    promo_id: r.data.promo_id,
                    promo_code: r.data.code,
                    discount_type: r.data.discount_type,
                    discount_value: r.data.discount_value,
                    discount_amount: r.data.discount_amount,
                    final_charge: Math.max(0, Number(r.data.final_charge || 0) + fee + sFee),
                };
            });
            setCode(clean);
            toast.success(`Applied: ${r.data.name}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Code not valid");
        } finally { setBusy(false); }
    };

    const apply = () => applyCode(code);

    const clear = () => {
        setCode("");
        setPricing((p) => {
            const fee = Number(p.priority_fee || 0);
            const sFee = Number(p.scanner_pickup_fee || 0);
            const labLocal = Number(p.lab_charge || 0);
            return {
                ...p,
                promo_id: null, promo_code: "", discount_type: null,
                discount_value: 0, discount_amount: 0,
                final_charge: Math.max(0, labLocal + fee + sFee),
            };
        });
    };

    return (
        <div data-testid="case-wizard-pricing-and-offers">
            <div className="mb-3">
                <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Pricing &amp; offers</span>
            </div>
            <div className="relative rounded-2xl border border-border bg-card overflow-hidden">
                <div className="px-5">
                    {/* Tier row */}
                    <div className="py-3.5 flex items-center justify-between gap-4" data-testid="pricing-row-tier">
                        <div className="text-[13px] text-foreground/85 min-w-0">
                            {pricing.tier_name || "Treatment tier"}
                        </div>
                        <div className="font-display text-[14px] font-semibold tabular-nums shrink-0 text-foreground">
                            {fmtINR(lab)}
                        </div>
                    </div>
                    {/* Manufacturing speed row */}
                    <div className="pb-3.5 flex items-center justify-between gap-4" data-testid="pricing-row-priority">
                        <div className="text-[13px] text-foreground/85">Manufacturing speed</div>
                        <div className="shrink-0">
                            {priorityFee > 0 ? (
                                <span className="font-display text-[14px] font-semibold tabular-nums text-foreground">+ {fmtINR(priorityFee)}</span>
                            ) : (
                                <span className="text-[13px] text-muted-foreground italic">Included</span>
                            )}
                        </div>
                    </div>

                    {/* iter97.51 — IO Scanning Service add-on row. */}
                    {scannerRequested && scannerFee > 0 && (
                        <div className="pb-3.5 flex items-center justify-between gap-4" data-testid="pricing-row-scanner">
                            <div className="text-[13px] text-foreground/85">IO Scans by Invisique™</div>
                            <div className="shrink-0">
                                <span className="font-display text-[14px] font-semibold tabular-nums text-foreground">+ {fmtINR(scannerFee)}</span>
                            </div>
                        </div>
                    )}

                    {/* iter98.59 — Lab-visit scanning fee row. */}
                    {scanFeeSummary && scanFeeSummary.fee > 0 && (
                        <LabVisitScanFeeRow summary={scanFeeSummary} />
                    )}

                    {/* Divider */}
                    <div className="border-t border-border/70 -mx-5" />

                    {/* Live offers / promo */}
                    <div className="py-4 space-y-3">
                        {applied ? (
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
                                    <div className="text-[13px] font-semibold truncate">
                                        {pricing.promo_code}{" "}
                                        <span className="text-muted-foreground font-normal">
                                            applied · {pricing.discount_type === "percentage"
                                                ? `${pricing.discount_value}% off`
                                                : `${fmtINR(pricing.discount_value)} off`}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={clear}
                                    className="shrink-0 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-md transition-colors"
                                    data-testid="case-wizard-promo-clear"
                                >
                                    Remove
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="text-[9.5px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                                    Live offers — tap to apply
                                </div>
                                {eligiblePromos.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5" data-testid="case-wizard-promo-chips">
                                        {eligiblePromos.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                disabled={busy}
                                                onClick={() => applyCode(p.code)}
                                                data-testid={`promo-chip-${p.code}`}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-200 px-2.5 py-1 text-[11.5px] font-semibold text-emerald-800 transition disabled:opacity-50"
                                            >
                                                <Tag size={11} weight="fill" className="text-emerald-600" />
                                                {p.code} · {p.discount_type === "percentage" ? `${p.discount_value}% off` : `${fmtINR(p.discount_value)} off`}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <Input
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        placeholder="Or type a promo code"
                                        className="h-9 text-[12.5px] font-mono uppercase tracking-wider"
                                        data-testid="case-wizard-promo-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={apply}
                                        disabled={busy || !code.trim()}
                                        className="h-9 px-4 text-[12.5px]"
                                        data-testid="case-wizard-promo-apply"
                                    >
                                        {busy ? "Checking…" : "Apply"}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Divider + discount line (only when applied) */}
                    {applied && discount > 0 && (
                        <>
                            <div className="border-t border-border/70 -mx-5" />
                            <div className="py-3 flex items-center justify-between gap-3" data-testid="pricing-row-discount">
                                <div className="text-[13px] text-foreground/85">
                                    Discount ({pricing.promo_code}
                                    {pricing.discount_type === "percentage" ? ` · ${pricing.discount_value}%` : ""})
                                </div>
                                <div className="font-display text-[14px] font-semibold tabular-nums text-emerald-700">
                                    − {fmtINR(discount)}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Total — matches Tier card price typography */}
                    <div className="border-t border-border -mx-5" />
                    <div className="py-3.5 flex items-baseline justify-between gap-3" data-testid="pricing-row-total">
                        <div className="font-display text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Total</div>
                        <div className="font-display text-[22px] font-bold tabular-nums text-foreground" data-testid="pricing-total-amount">
                            {fmtINR(final)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


/* PrescriptionPreviewCard — iter98.99am
 *
 * Slim companion CTA shown on Step 6 underneath the "Preview the case"
 * hero. Opens a dialog rendering a fidelity preview of what the
 * prescription PDF will look like (mirrors the layout produced by
 * /app/backend/prescription_pdf.py). Helps the doctor see the LAB-
 * facing artefact BEFORE they commit to Sign + Submit, since the
 * merged CTA gives no other opportunity to inspect the Rx.
 */
export function PrescriptionPreviewCard({ patient, doctorProfile, draftCase, diagnosis, notes, clinical, rx, planTimeline, planningMode }) {
    const isSelfPlan = planningMode === "doctor";
    const archRaw = (clinical?.arch || "both").toLowerCase();
    const archLabel = { upper: "Upper arch only", lower: "Lower arch only", both: "Both arches" }[archRaw] || archRaw;
    const upperA = Number(planTimeline?.aligner_count_upper || 0);
    const lowerA = Number(planTimeline?.aligner_count_lower || 0);
    /* iter98.99dc — Treatment depth is max(U,L) stages (1 stage = 1 U + 1 L aligner);
       physicalA is the total piece count that ships. */
    const stagesA = Math.max(upperA, lowerA);
    const physicalA = upperA + lowerA;
    const wearDays = Number(planTimeline?.wear_schedule_default_days || 0);

    const directiveLbl = (v, fallback = "per_plan") => ({
        per_plan: "As per the plan",
        modified: "Modified — see lab notes",
        none: "None",
    }[(v || fallback).toLowerCase()] || "—");
    const ipr = directiveLbl(rx?.ipr_directive);
    const att = directiveLbl(rx?.attachments_directive);
    const ela = directiveLbl(rx?.elastics_directive);

    const patientFlags = (rx?.patient_flags && rx.patient_flags.length)
        ? rx.patient_flags.map((f) => String(f).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())).join(", ")
        : "None reported";
    const complexity = (rx?.case_complexity || "moderate").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const shipRaw = (rx?.shipment_preference || "all_at_once").toLowerCase();
    const ship = ({ all_at_once: "Ship all aligners at once", phased: "Phased shipment" }[shipRaw]) || shipRaw;

    return (
        <Dialog>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="group w-full text-left rounded-xl border border-border bg-card hover:border-primary/30 hover:bg-primary/[0.03] transition-colors px-4 py-3 flex items-center gap-3"
                    data-testid="case-wizard-rx-preview-launcher"
                >
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center ring-1 ring-primary/20">
                        <Prescription size={16} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-display text-[13.5px] font-semibold text-foreground leading-tight">
                            Preview prescription
                        </div>
                        <div className="text-[11.5px] text-muted-foreground leading-snug mt-0.5">
                            See exactly what the lab receives — the same document you&apos;re about to sign.
                        </div>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold text-primary">
                        Preview
                        <CaretRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                    </span>
                </button>
            </DialogTrigger>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 bg-secondary/20"
                data-testid="case-wizard-rx-preview-dialog"
            >
                <DialogTitle className="sr-only">Prescription preview</DialogTitle>
                <DialogDescription className="sr-only">
                    This is exactly what the lab and your audit log will see once you sign. Close to make any edits.
                </DialogDescription>

                {/* Document — paper card mirroring the downloaded
                    Prescription PDF aesthetic. */}
                <article className="m-4 bg-card rounded-2xl shadow-[0_8px_32px_-8px_rgba(15,23,42,0.12)] ring-1 ring-border/70 overflow-hidden">

                    {/* Letterhead */}
                    <header
                        className="relative px-6 sm:px-8 pt-6 pb-5 bg-gradient-to-br from-primary/[0.06] via-card to-card border-b border-border/70"
                        data-testid="rx-preview-letterhead"
                    >
                        <div
                            aria-hidden
                            className="absolute inset-0 pointer-events-none opacity-[0.04]"
                            style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "24px 24px" }}
                        />
                        <div className="relative flex items-start justify-between gap-4 flex-wrap">
                            <InvisiqueLogo size="lg" showTagline />
                            <div className="text-right">
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary">
                                    Prescription
                                </div>
                                <div className="font-display text-xl font-bold text-foreground tracking-tight leading-tight mt-1">
                                    Treatment Order
                                </div>
                                <div className="text-[11.5px] text-muted-foreground mt-1 tabular-nums">
                                    Draft · {draftCase?.code || "(code on save)"} · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                </div>
                            </div>
                        </div>
                        <div className="relative mt-4 flex items-center justify-between gap-3 flex-wrap">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/80 ring-1 ring-border text-[11px] font-semibold text-foreground/80">
                                <SealCheck size={13} weight="fill" className="text-primary" />
                                Same document the lab and audit log will see
                            </div>
                            <span
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-bold uppercase tracking-[0.14em] bg-primary/10 text-primary border-primary/30"
                            >
                                <Sparkle size={11} weight="fill" /> {isSelfPlan ? "Self-Plan" : "CoE Planning"}
                            </span>
                        </div>
                    </header>

                    {/* Identity block — Patient + Prescribing Doctor */}
                    <section className="px-6 sm:px-8 py-5 border-b border-border/70 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                                Patient
                            </div>
                            <div className="flex items-start gap-3">
                                {/* iter101.58 — PatientAvatar `xl` (64px) so the
                                    Rx preview identity photo matches the doctor
                                    portal's My Patients + Case Detail headers. */}
                                <PatientAvatar patient={patient} size="xl" className="ring-2 ring-primary/20" />
                                <div className="min-w-0">
                                    <div className="font-display text-base font-bold text-foreground leading-tight">
                                        {patient?.name || "—"}
                                    </div>
                                    <div className="text-[12px] text-muted-foreground flex items-center gap-x-3 gap-y-1 flex-wrap mt-1">
                                        {patient?.phone && <span className="tabular-nums">{patient.phone}</span>}
                                        {patient?.dob && <span><strong className="text-foreground/80">DOB</strong> · {patient.dob}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                                Prescribing Doctor
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="shrink-0 w-16 h-16 rounded-full bg-secondary text-foreground/80 grid place-items-center ring-1 ring-border">
                                    <Stethoscope size={24} weight="duotone" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-display text-base font-bold text-foreground leading-tight">
                                        {doctorProfile?.name || "—"}
                                    </div>
                                    <div className="text-[12px] text-muted-foreground mt-1 space-y-0.5">
                                        {doctorProfile?.qualification && <div>{doctorProfile.qualification}</div>}
                                        {doctorProfile?.registration_number && (
                                            <div className="tabular-nums">Reg. No. {doctorProfile.registration_number}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Document body — sections */}
                    <div className="px-6 sm:px-8 py-6 space-y-5 bg-card">
                        <ReviewBlock title="Treatment">
                            <Row k="Diagnosis" v={diagnosis || "—"} />
                            <Row k="Arches in treatment" v={archLabel} />
                            <Row
                                k="Stage count"
                                v={stagesA ? `${stagesA} stages · ${upperA}U + ${lowerA}L = ~${physicalA} physical aligners` : (isSelfPlan ? "—" : "To be authored by Invisique™ planner")}
                            />
                            <Row
                                k="Wear schedule"
                                v={wearDays ? `${wearDays} days per stage` : (isSelfPlan ? "—" : "To be set by planner")}
                            />
                        </ReviewBlock>

                        <ReviewBlock title="Prescription & Lab Instructions">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                                <Row k="IPR directive" v={ipr} />
                                <Row k="Attachments directive" v={att} />
                                <Row k="Elastics directive" v={ela} />
                                <Row k="Shipment preference" v={ship} />
                                <Row k="Patient flags" v={patientFlags} />
                                <Row k="Case complexity" v={complexity} />
                            </div>
                            <div className="mt-3 pt-3 border-t border-border/70 space-y-1.5">
                                <Row k="Do-not-move teeth" v={rx?.do_not_touch_teeth || "None specified"} />
                                {(rx?.shipment_notes || "").trim() && <Row k="Phase notes" v={rx.shipment_notes} />}
                                <Row k="Custom lab notes" v={(rx?.lab_notes || notes || "—")} />
                            </div>
                        </ReviewBlock>

                        {/* Signature placeholder */}
                        <div className="pt-2">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1.5">Signature</div>
                            <div
                                className="rounded-lg border border-dashed border-primary/30 bg-primary/[0.04] px-4 py-3 text-[11.5px] text-foreground/70 italic"
                                data-testid="case-wizard-rx-preview-sig-placeholder"
                            >
                                Your signature will appear here once you sign &amp; submit.
                            </div>
                            <p className="mt-3 text-[10px] text-muted-foreground/80 leading-snug">
                                By signing, the prescribing doctor certifies the diagnosis, treatment plan, and lab instructions above
                                comply with applicable medical regulations (IT Act, NMC Guidelines) and that explicit patient consent
                                has been obtained.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <footer className="px-6 sm:px-8 py-3 border-t border-border/70 bg-secondary/30 text-center">
                        <div className="text-[10px] text-muted-foreground/80 tracking-wider">
                            Draft prescription preview — sign &amp; submit the case to dispatch this order to the lab.
                        </div>
                    </footer>
                </article>
            </DialogContent>
        </Dialog>
    );
}

export function PreviewBlock({ title, children }) {
    return (
        <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1.5">{title}</div>
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}

export function PreviewRow({ k, v }) {
    return (
        <div className="flex items-start gap-3 text-[12.5px]">
            <div className="min-w-[140px] text-muted-foreground font-medium">{k}</div>
            <div className="text-foreground/90 flex-1">{v}</div>
        </div>
    );
}


/* CoeOrderPreviewCard — iter98.99al · expanded iter101.17
 *
 * Read-only mini summary doctors see on Step 6 (Review & Submit) of a
 * CoE-mode case. The full Payment & Offers card is hidden because the
 * planner — not the doctor — picks the treatment tier. To keep the
 * doctor confident about EVERYTHING they've committed to in Step 1,
 * we now surface all of their selections inline (not just the two
 * planner-independent line items with a price):
 *   • Scan source        → I'll upload / IO Scans by Invisique™
 *   • Planning mode      → Invisique™ CoE / Self-Plan
 *   • Compliance         → Yes (+ fee if any) / No · self-managed
 *   • Manufacturing      → label + add-on price (or "Included")
 *   • Treatment tier     → "Pending planner" pill
 *   • IO Scanner fee     → only when the doctor opted in (fold-in row)
 *
 * After the planner publishes the plan and sends the case for the
 * doctor's approval, `PlanApprovalHero.ApprovalPricingPanel` renders
 * the full breakdown (with promo input + applied discount + total)
 * inside the doctor approval screen. This card and that card share
 * the same visual rhythm so the transition feels continuous.
 */
export function CoeOrderPreviewCard({
    pricing = {},
    ioScannerFee = 3000,
    scanSource = "",
    planningMode = "",
    coeSupportCompliance = null,
    complianceFee = 0,
}) {
    const priorityFee = Number(pricing.priority_fee || 0);
    const priorityLabel = pricing.priority_label || "Standard";
    const scannerRequested = !!pricing.scanner_pickup_requested;
    const scannerFee = Number(pricing.scanner_pickup_fee || 0) || (scannerRequested ? Number(ioScannerFee || 0) : 0);
    // Copy for the scan source row — mirrors the wording used on Step 1
    const scanSourceLabel = scanSource === "io_service"
        ? "IO Scans by Invisique™"
        : scanSource === "upload"
            ? "Doctor-uploaded scans"
            : "Not picked yet";
    const planningLabel = planningMode === "coe"
        ? "Invisique™ Planning Team"
        : planningMode === "doctor"
            ? "Self-Plan (doctor authors)"
            : "Not picked yet";
    return (
        <div data-testid="case-wizard-coe-order-preview">
            <div className="mb-3">
                <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Order preview</span>
            </div>
            <div className="relative rounded-2xl border border-border bg-card overflow-hidden">
                <div className="px-5 divide-y divide-border/60">
                    {/* Scan source row */}
                    <div className="py-3 flex items-center justify-between gap-4" data-testid="coe-order-row-scan-source">
                        <div className="text-[13px] text-foreground/85">Scan source</div>
                        <div className="shrink-0 text-right">
                            <div className="text-[13px] font-semibold text-foreground">{scanSourceLabel}</div>
                            {scanSource === "io_service" && scannerFee > 0 && (
                                <div className="text-[11px] text-muted-foreground tabular-nums">+ {fmtINR(scannerFee)} scanner pickup</div>
                            )}
                        </div>
                    </div>
                    {/* Planning mode row */}
                    <div className="py-3 flex items-center justify-between gap-4" data-testid="coe-order-row-planning">
                        <div className="text-[13px] text-foreground/85">Treatment planning</div>
                        <div className="shrink-0 text-right">
                            <div className="text-[13px] font-semibold text-foreground">{planningLabel}</div>
                        </div>
                    </div>
                    {/* Compliance monitoring row */}
                    <div className="py-3 flex items-center justify-between gap-4" data-testid="coe-order-row-compliance">
                        <div className="text-[13px] text-foreground/85">Compliance Monitoring &amp; Support</div>
                        <div className="shrink-0 text-right">
                            {coeSupportCompliance === true ? (
                                <>
                                    <div className="text-[13px] font-semibold text-foreground">Invisique™ manages it</div>
                                    <div className="text-[11px] tabular-nums">
                                        {Number(complianceFee) > 0
                                            ? <span className="text-foreground/80">+ {fmtINR(complianceFee)}</span>
                                            : <span className="text-emerald-700">Included</span>}
                                    </div>
                                </>
                            ) : coeSupportCompliance === false ? (
                                <div className="text-[13px] font-semibold text-foreground">Self-managed</div>
                            ) : (
                                <div className="text-[13px] italic text-muted-foreground">Not picked yet</div>
                            )}
                        </div>
                    </div>
                    {/* Manufacturing speed row */}
                    <div className="py-3 flex items-center justify-between gap-4" data-testid="coe-order-row-priority">
                        <div className="text-[13px] text-foreground/85">Manufacturing speed</div>
                        <div className="shrink-0 text-right">
                            <div className="text-[13px] font-semibold text-foreground">{priorityLabel}</div>
                            <div className="text-[11px] tabular-nums">
                                {priorityFee > 0
                                    ? <span className="text-foreground/80">+ {fmtINR(priorityFee)}</span>
                                    : <span className="text-muted-foreground italic">Included</span>}
                            </div>
                        </div>
                    </div>
                    {/* Treatment tier row — pending planner */}
                    <div className="py-3 flex items-center justify-between gap-4" data-testid="coe-order-row-tier">
                        <div className="text-[13px] text-foreground/85">Treatment tier</div>
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 ring-1 ring-amber-200 px-2.5 py-0.5">
                            <Clock size={11} weight="fill" className="text-amber-700" />
                            <span className="text-[11px] font-semibold tracking-wide uppercase text-amber-800">Pending planner</span>
                        </div>
                    </div>
                    {/* Helper line */}
                    <div className="py-3 flex items-start gap-2">
                        <Info size={13} className="text-muted-foreground shrink-0 mt-0.5" weight="fill" />
                        <p className="text-[11.5px] text-muted-foreground leading-snug">
                            Your Invisique™ planner will set the treatment tier after they finish authoring the plan.
                            When they send the plan for your approval, you&apos;ll see the full breakdown with promo codes and final pricing.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ─────── Priority / STAT picker ───────
 * Doctor picks how fast they need the case. Standard is the implicit
 * baseline (no add-on). Rush / STAT carry an add-on fee that flows
 * into the case's invoice. The selected priority also bumps the case
 * row's top-level `priority` field — the production workflow engine
 * already reads it for stage ordering, so picking STAT here is enough
 * to push the case to the top of the lab queue.
 */
export function PriorityPicker({ priorityTiers, pricing, setPricing, hideHeader = false }) {
    if (!priorityTiers || priorityTiers.length === 0) return null;
    // iter98.99aa — Defensive dedupe by `code` so a stray duplicate
    // (e.g. backend regression that returns both DEFAULT_STANDARD and a
    // DB row with code='standard') can never crash the picker with a
    // React duplicate-key warning + two visually-identical cards. The
    // first occurrence wins, which matches the backend's sort order
    // (sort_order ASC, price ASC).
    const seenCodes = new Set();
    const dedupedTiers = priorityTiers.filter((t) => {
        const code = (t.code || "").toLowerCase();
        if (seenCodes.has(code)) return false;
        seenCodes.add(code);
        return true;
    });
    // iter98.99x — When the doctor hasn't picked yet (`priority_code === ""`)
    // we leave `selectedId` as a sentinel that matches none of the tier
    // ids/null. Previously the default `priority_tier_id: null` auto-
    // selected the Standard card (its id is null too) and the doctor
    // never had to consciously pick a speed.
    const hasPicked = !!(pricing.priority_code || "").trim();
    const selectedId = hasPicked ? (pricing.priority_tier_id ?? null) : Symbol("none");
    const onPick = (tier) => {
        const fee = Number(tier.price_inr || 0);
        setPricing((p) => {
            // iter92.10 — final_charge MUST include priority_fee. Before this
            // fix it was lab - discount only; the UI showed "+₹X" but the
            // invoice generator never billed it.
            // iter97.51 — also preserve scanner_pickup_fee in the running
            // total so changing priority doesn't silently drop the IO
            // scanning add-on the doctor already toggled on.
            const lab = Number(p.lab_charge || 0);
            const discount = Number(p.discount_amount || 0);
            const scannerFee = Number(p.scanner_pickup_fee || 0);
            const finalCharge = Math.max(0, lab - discount + fee + scannerFee);
            return {
                ...p,
                priority_tier_id: tier.id ?? null,            // null = Standard
                priority_code: tier.code,
                priority_label: tier.label,
                priority_fee: fee,
                priority_tat_days: Number(tier.tat_days || 0),
                final_charge: finalCharge,
            };
        });
    };
    /* iter98.99ab — Card copy source. All visible content below comes
       EITHER from the admin's priority-tier configuration (label,
       card_subtitle, card_bullets, card_badge, card_accent, tat_days,
       price_inr) OR from the doctor's selection — nothing here is
       hardcoded fallback text. Admin owns 100% of the card content. */
    return (
        <div
            className={`${hideHeader ? "space-y-3" : "border-t border-border pt-4 space-y-3"}`}
            data-testid="case-wizard-priority-section"
        >
            {/* iter98.99h — Richer header. Doctors used to ask "what is
                manufacturing speed?" because the label alone reads
                ambiguous. New copy explicitly anchors it to patient
                treatment-start date, which is the outcome doctors
                actually care about.
                iter98.99i — Header is now skipped when an outer
                `Step1SectionHeader` is rendering the numbered chrome
                in Step 1. Self-Plan Step 6 still renders the inline
                header (no numbered chip needed there). */}
            {!hideHeader && (
                <div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Manufacturing speed</span>
                        <span className="text-[12px] text-muted-foreground">Add-on fees ship with this case&apos;s invoice.</span>
                    </div>
                    <p className="text-[12px] text-foreground/75 mt-1.5 leading-snug max-w-3xl">
                        How fast we manufacture &amp; ship your patient&apos;s aligners after the plan is approved.{" "}
                        <span className="text-muted-foreground">
                            Faster shipping = earlier treatment start. Pick the option that matches your patient&apos;s timeline.
                        </span>
                    </p>
                </div>
            )}
            {/* iter98.99y → iter98.99ab — Priority cards use the same
                ModeCard grammar as Step 1 Submission Mode (eyebrow +
                title + admin-set subtitle/bullets + a tinted meta
                footer). Hardcoded fallback subtitles / bullets / best-for
                were removed so admin owns 100% of the visible content;
                an empty admin field just renders empty. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {dedupedTiers.map((t) => {
                    const id = t.id ?? null;
                    const selected = id === selectedId;
                    const fee = Number(t.price_inr || 0);
                    /* Icon mapping is the ONE deterministic visual cue we
                       keep client-side — purely so the doctor reads the
                       three options at a glance. Admin-set `card_accent`
                       still tints everything else. */
                    const codeLc = (t.code || "").toLowerCase();
                    const TierIcon = codeLc.includes("stat") || codeLc.includes("urgent")
                        ? Lightning
                        : codeLc.includes("rush") || codeLc.includes("express")
                            ? Clock
                            : Package; // standard / unknown → Package (avoids the duplicate-check visual on selected state)
                    /* Admin-configured copy ONLY. Empty when admin hasn't
                       customized that tier — the picker simply omits the
                       row instead of inventing fallback marketing copy. */
                    const adminBadge = (t.card_badge || "").trim();
                    const subtitle = (t.card_subtitle || "").trim();
                    const customBullets = Array.isArray(t.card_bullets)
                        ? t.card_bullets.filter((b) => (b || "").trim())
                        : [];
                    /* iter98.99f4 — When the admin hasn't explicitly set
                       `card_accent`, fall back to `"amber"` (matching the
                       Section 04 header) rather than `"primary"` so the
                       Manufacturing Speed selected card doesn't visually
                       collide with Section 01 / 02 teal accents. Admin's
                       explicit per-tier accent still wins. */
                    const accent = t.card_accent || "amber";
                    const feeLabel = fee > 0 ? `+${fmtINR(fee)}` : "Included";
                    const tatLabel = t.tat_days != null ? `${t.tat_days}d after plan approval` : "";
                    return (
                        <ModeCard
                            key={t.code}
                            selected={selected}
                            onClick={() => onPick(t)}
                            eyebrow=""
                            title={(t.label || "").replace(/(^|\s)\S/g, (m) => m.toUpperCase())}
                            subtitle={subtitle}
                            badge={adminBadge}
                            badgeColor={t.card_badge_color}
                            accent={accent}
                            icon={TierIcon}
                            bullets={customBullets}
                            meta={[
                                { k: "Add-on fee", v: feeLabel },
                                { k: "Lead time",  v: tatLabel },
                            ]}
                            testid={`case-wizard-priority-${t.code}`}
                        />
                    );
                })}
            </div>
        </div>
    );
}



/* iter97.77 Wave 2 — StepRecords + ScanProvenanceSection import removed.
   Step 3 (Scans & Photos) is now rendered exclusively by
   `ScansPhotosStep` (see /components/case/ScansPhotosStep.jsx) — a
   purpose-built accordion-driven layout that embeds scan provenance,
   raw scan uploads, planning-software export (self-plan only),
   intraoral/extraoral/x-ray uploads, a confirmation strip, and an
   upload summary glance into a single cohesive surface. */


export function SlotUploader({ slot, state, onPick, onRemove }) {
    const uploaded = state?.uploaded;
    const uploading = state?.uploading;
    const removing = state?.removing;
    const inputId = `wizard-slot-${slot.key}`;
    return (
        <div
            className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${uploaded ? "border-emerald-200 bg-emerald-50/40" : "border-border hover:bg-secondary/30"}`}
            data-testid={`case-wizard-slot-${slot.key}`}
        >
            <input
                id={inputId}
                type="file"
                accept={slot.accepts}
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
            />
            <label
                htmlFor={inputId}
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 cursor-pointer ${uploaded ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}
                aria-label={uploaded ? "Replace file" : "Upload file"}
            >
                {uploaded ? <Check size={16} weight="bold" /> : <Upload size={16} />}
            </label>
            <label htmlFor={inputId} className="flex-1 min-w-0 cursor-pointer">
                <div className="text-sm font-medium truncate">{slot.label}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                    {uploading ? "Uploading…" : removing ? "Removing…" : state?.name || `Click to upload (${slot.accepts})`}
                </div>
            </label>
            {uploaded && !uploading && (
                <div className="flex items-center gap-1 shrink-0">
                    <label
                        htmlFor={inputId}
                        className="text-[11px] font-semibold text-primary px-2 py-1 rounded-full hover:bg-primary/10 cursor-pointer"
                        data-testid={`case-wizard-slot-${slot.key}-replace`}
                    >
                        Replace
                    </label>
                    <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); onRemove(); }}
                        disabled={removing}
                        className="w-6 h-6 rounded-full grid place-items-center text-muted-foreground hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
                        title="Remove file"
                        data-testid={`case-wizard-slot-${slot.key}-remove`}
                    >
                        <X size={11} weight="bold" />
                    </button>
                </div>
            )}
        </div>
    );
}


/* ─────────── Planning software export — hero card ───────────
 * iter97.61 — Promoted from a small inline section to a hero card.
 * This file is the machine-readable companion to the per-tooth plan and
 * is what the lab actually manufactures from, so the visual weight now
 * matches that importance. Layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  [3D icon]  Planning software export                       │
 *   │             Direct from ClinCheck · 3Shape · Maestro …     │
 *   │  ────────────────────────────────────────────────────────  │
 *   │  Supported planning suites (chips)                         │
 *   │  Supported file types (chips with format icons)            │
 *   │  ────────────────────────────────────────────────────────  │
 *   │  [Dashed upload zone — drag-and-drop or click]             │
 *   │  Uploaded files (each as a row with replace/remove)        │
 *   └────────────────────────────────────────────────────────────┘
 */
export function PlanningSoftwareExportCard({ slots, uploads, onUpload, onRemove }) {
    const allFormats = useMemo(() => {
        const set = new Set();
        slots.forEach((s) => (s.accepts || "").split(",").forEach((a) => {
            const t = a.trim();
            if (t) set.add(t);
        }));
        return Array.from(set);
    }, [slots]);

    const formatChips = useMemo(() => {
        const groups = [
            { label: "ZIP project", match: /zip/i, Icon: FileZip, tone: "bg-amber-50 text-amber-700 border-amber-200" },
            { label: "STL / OBJ / PLY", match: /stl|obj|ply/i, Icon: Cube, tone: "bg-violet-50 text-violet-700 border-violet-200" },
            { label: "PDF screenshots", match: /pdf/i, Icon: FilePdf, tone: "bg-rose-50 text-rose-700 border-rose-200" },
            { label: "MP4 walk-through", match: /mp4|video/i, Icon: FileVideo, tone: "bg-sky-50 text-sky-700 border-sky-200" },
        ];
        return groups.filter((g) => allFormats.some((f) => g.match.test(f)));
    }, [allFormats]);

    const suiteChips = [
        { label: "ClinCheck", desc: "Align / Invisalign" },
        { label: "3Shape", desc: "TRIOS, Ortho System" },
        { label: "Maestro 3D", desc: "Ortho Studio" },
        { label: "OnyxCeph", desc: "Image Instruments" },
        { label: "Other", desc: "Any digital setup" },
    ];

    return (
        <section className="relative overflow-hidden rounded-[20px] border border-emerald-200/70 bg-gradient-to-br from-emerald-50/60 via-card to-card shadow-[0_1px_2px_rgba(16,185,129,0.04)]" data-testid="planning-software-export-card">
            {/* Decorative grid background */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.04]"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, #064e3b 1px, transparent 1px), linear-gradient(to bottom, #064e3b 1px, transparent 1px)",
                    backgroundSize: "24px 24px",
                }}
            />

            {/* Header */}
            <div className="relative px-6 pt-6 pb-5 flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white shadow-md shadow-emerald-500/20">
                    <Cube size={26} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-700">
                            Machine-readable plan
                        </div>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-600 text-white">
                            <Sparkle size={10} weight="fill" /> Recommended
                        </span>
                    </div>
                    <h3 className="text-[18px] font-bold text-foreground mt-1 leading-snug">
                        Planning software export
                    </h3>
                    <p className="text-[13px] text-muted-foreground mt-1 leading-relaxed max-w-[640px]">
                        Upload your digital setup so the lab manufactures from your actual plan — not from a re-interpretation. Faster turnaround, exact aligner geometry, fewer revisions.
                    </p>
                </div>
            </div>

            {/* Supported suites — chip row */}
            <div className="relative px-6 pb-4">
                <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground mb-2">
                    Works with
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {suiteChips.map((s) => (
                        <span
                            key={s.label}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/80 border border-emerald-200/60 text-[12px] font-semibold text-emerald-900"
                            title={s.desc}
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {s.label}
                        </span>
                    ))}
                </div>
            </div>

            {/* Supported file formats */}
            {formatChips.length > 0 && (
                <div className="relative px-6 pb-5">
                    <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground mb-2">
                        Accepts
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {formatChips.map(({ label, Icon, tone }) => (
                            <span
                                key={label}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-medium ${tone}`}
                            >
                                <Icon size={12} weight="duotone" />
                                {label}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Upload slots — proper hero zone */}
            <div className="relative px-6 pb-6">
                <div className="rounded-2xl border-2 border-dashed border-emerald-300/70 bg-white/70 p-3 space-y-2">
                    {slots.map((slot) => (
                        <SlotUploader
                            key={slot.key}
                            slot={slot}
                            state={uploads[slot.key]}
                            onPick={(f) => onUpload(slot.key, f)}
                            onRemove={() => onRemove(slot.key)}
                        />
                    ))}
                </div>
                <div className="mt-3 flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed">
                    <ShieldCheck size={14} weight="duotone" className="mt-0.5 shrink-0 text-emerald-600" />
                    <p>
                        Files are encrypted at rest and only accessible to the lab assigned to this case. You can replace or remove uploads at any point before manufacturing kicks off.
                    </p>
                </div>
            </div>
        </section>
    );
}


/* iter88 — restructured wizard splits the legacy single StepPrescription
 * into 3 focused steps. Each step owns one well-bounded concept:
 *   Step 4 StepTreatmentPlan: high-level objectives + tooth movements
 *     + 3D planning software upload (self-plan only). CoE mode shows a
 *     handoff callout for the per-tooth detail.
 *   Step 5 StepAttachments: per-tooth attachments + precision cuts +
 *     bite ramps (self-plan only). CoE shows the same handoff callout.
 *   Step 6 StepLabInstructions: material preference, lab notes, IPR /
 *     elastics preferences, do-not-touch teeth, esthetic goals. Both
 *     modes can fill these — they ride along with the case to the lab
 *     regardless of who authored the plan.
 */

export function CoeHandoffCallout({ subject }) {
    return (
        <div
            className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/[0.04] p-4 text-sm"
            data-testid="case-wizard-coe-handoff-callout"
        >
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/15 grid place-items-center shrink-0">
                    <GitBranch size={16} weight="bold" className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-foreground">
                        Your Invisique™ planner authors the {subject}
                    </div>
                    <p className="text-[12.5px] text-muted-foreground mt-1 leading-snug">
                        Our specialists set this up after reviewing your imaging and
                        clinical notes. You'll review and approve the full plan before
                        manufacturing begins.
                    </p>
                    <p className="text-[11px] text-foreground/70 mt-2">
                        Want hands-on control instead? Go back to Step 1 and switch to <strong>Self-plan</strong>.
                    </p>
                </div>
            </div>
        </div>
    );
}
